import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { LaborItem } from '../../../../../src/store/models/LaborModel'
import { Grid } from '@mui/material'
import { Translate } from 'common'
import styled from 'styled-components'
import { defaultTheme } from '../../../../../src/theme/index'
import { breakpoints } from '../../../../../src/theme/breakpoints'
import { useLaborLookup } from '../../../../../src/store/labor/LaborStore'
import PartsTableHeader from './order/PartsTableHeader'
import { StyledGridTitle, Table } from './SharedCssStyle'

interface LaborConfirmationItemsInterface {
  laborItems: Array<LaborItem>
}

export const LaborConfirmationItems = observer(
  (props: LaborConfirmationItemsInterface): ReactElement | null => {
    const { calculateLaborCost } = useLaborLookup()
    const { laborItems } = props

    const formattedPrice = (labor: LaborItem): string =>
      calculateLaborCost(labor).toFixed(2)
    if (laborItems?.length <= 0) {
      return null
    }
    return (
      <>
        <Grid container className="mt-5 mb-3 px-4">
          <StyledGridTitle item>{Translate('labor')}</StyledGridTitle>
        </Grid>
        <Table>
          <PartsTableHeader
            columns={[
              {
                name: 'laborId',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
              {
                name: 'description',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px', width: '25%' },
              },
              {
                name: 'rate',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
              {
                name: 'hours',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
              {
                name: 'wtyHours',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
              {
                name: 'skillLevel',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
              {
                name: 'total',
                align: 'left',
                visible: true,
                style: { paddingLeft: '24px' },
              },
            ]}
            imageColumnRequired={false}
          />
          <tbody>
            {laborItems?.map((labor) => {
              return (
                <Row key={labor.laborId}>
                  <Td>{labor.laborId}</Td>
                  <Td>
                    {labor.laborDescription} <br />
                    <AdditionalText>{labor.details}</AdditionalText>
                  </Td>
                  <Td>{labor.laborRate}</Td>
                  <Td>{labor.hours}</Td>
                  <Td>{labor.warrantyHrs}</Td>
                  <Td>{labor.skillLevel}</Td>
                  <Td>${formattedPrice(labor)}</Td>
                </Row>
              )
            })}
          </tbody>
        </Table>
      </>
    )
  }
)

const Row = styled.tr`
  text-align: initial;
  background: ${defaultTheme.colors.white};
  box-shadow: inset 0px -1px 0px ${defaultTheme.colors.border};
  border: 24px;
  max-width: 100%;
  font-family: Roboto, serif;
  font-weight: bold;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    .hide-cell {
      display: none;
    }
    .td-column {
      display: block;
      flex-direction: column;
    }
  }
  @media (min-width: ${breakpoints.mobileLarge}px) {
    .text-visibility {
      display: none;
    }
  }
  @media print {
    text-decoration: none;
    font-weight: normal;
    font-size: 10px;
  }
`

const Td = styled.td`
  vertical-align: top;
  padding: 25px 24px;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    overflow: hidden;
  }
`

const AdditionalText = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  @media print {
    text-decoration: none;
    font-weight: normal;
    font-size: 10px;
  }
`
